import type {
  ContactImportExecutionStatus,
  ContactImportExecutionResult,
  ContactImportRow,
  ContactImportRowsPayload,
} from "../types";

const EXECUTION_STATUS_META: Record<
  ContactImportExecutionStatus,
  { label: string; color: string; imported: boolean }
> = {
  created: { label: "已导入", color: "green", imported: true },
  updated: { label: "已导入（补充电话）", color: "blue", imported: true },
  recorded: { label: "已导入（已记动态）", color: "cyan", imported: true },
  duplicate: { label: "未导入（重复）", color: "orange", imported: false },
  blocked: { label: "未导入（阻断）", color: "red", imported: false },
  skipped: { label: "未导入（已跳过）", color: "gray", imported: false },
  failed: { label: "未导入（失败）", color: "red", imported: false },
};

const UNPROCESSED_STATUS_META = { label: "未导入（未处理）", color: "orange", imported: false };

/** 返回单行执行结果的操作员可见状态。没有结果时表示执行批次未处理该行。 */
export function getContactImportExecutionStatusMeta(
  status?: ContactImportExecutionStatus,
): { label: string; color: string; imported: boolean } {
  return status ? EXECUTION_STATUS_META[status] : UNPROCESSED_STATUS_META;
}

/** 构建编辑行保存载荷；计算列和匹配结果永不回传。 */
export function buildEditedRowsPayload(
  version: number,
  rows: ContactImportRow[],
): ContactImportRowsPayload {
  return {
    version,
    rows: rows.map((row) => ({
      row_id: row.id,
      normalized: row.normalized,
      action_type: row.actionType,
      update_policy: row.updatePolicy,
      selected: row.selected,
    })),
  };
}

/** 只返回已勾选、非阻断且非跳过的执行行。 */
export function selectExecutableRowIds(rows: ContactImportRow[]): number[] {
  return rows
    .filter((row) => row.selected && isContactImportRowExecutable(row))
    .map((row) => row.id);
}

/** 仅允许新增或后端明确匹配出的补电话行执行。 */
export function isContactImportRowExecutable(row: ContactImportRow): boolean {
  return (
    !row.matchResult.execution_blocked &&
    ((row.actionType === "create" && row.matchResult.system_exists === "no") ||
      (row.actionType === "update" && row.matchResult.system_exists === "yes" &&
        row.matchResult.allowed_actions?.includes("update") === true)) &&
    row.executionStatus !== "succeeded" &&
    !["created", "updated", "recorded"].includes(row.executionResult?.status ?? "") &&
    row.validationStatus !== "blocked"
  );
}

/** 过滤当前导入口径不再处理的组织单元问题，仅保留操作者需要处理的原因。 */
export function getVisibleContactImportIssues(
  row: ContactImportRow,
): ContactImportRow["issues"] {
  return row.issues.filter((issue) => !isIgnoredOrganizationUnitIssue(issue.code));
}

/** 判断问题码是否属于当前导入口径忽略的组织单元范围。 */
function isIgnoredOrganizationUnitIssue(code: string): boolean {
  return (
    code.includes("ORG_UNIT") ||
    code === "CONTACT_IMPORT_DEPARTMENT_PARENT_REQUIRED" ||
    code === "CONTACT_IMPORT_MAJOR_PARENT_REQUIRED"
  );
}

/** 只返回当前预览页，避免一次渲染数万个可编辑表格控件。 */
export function paginateContactImportRows(
  rows: ContactImportRow[],
  page: number,
  pageSize: number,
): ContactImportRow[] {
  const safePageSize = Math.max(1, pageSize);
  const offset = (Math.max(1, page) - 1) * safePageSize;
  return rows.slice(offset, offset + safePageSize);
}

/** 把后端逐行结果合并回原表行，保留源数据和预览匹配信息。 */
export function applyContactImportExecutionResults(
  rows: ContactImportRow[],
  result: ContactImportExecutionResult,
): ContactImportRow[] {
  const resultByRow = new Map(result.rows.map((item) => [item.rowId, item]));
  return rows.map((row) => {
    const executionResult = resultByRow.get(row.id);
    return executionResult
      ? {
          ...row,
          selected: false,
          executionStatus: executionResult.status,
          executionResult,
        }
      : row;
  });
}

const ISSUE_MESSAGES: Record<string, string> = {
  PHONE_MATCHES_MULTIPLE_CONTACTS: "同一号码命中多个联系人",
  SOURCE_PHONES_MATCH_DIFFERENT_CONTACTS: "两个源号码命中不同联系人",
  PHONE_MATCHES_CROSS_ORGANIZATION: "号码命中其他学校联系人",
  SCHOOL_NAME_MATCHES_MULTIPLE_CONTACTS: "同一学校和姓名命中多个联系人",
  SAME_NAME_DIFFERENT_PHONE: "同校同名但电话不同",
  CONTACT_IMPORT_BATCH_CONTACT_NAME_CONFLICT: "本批次联系人全名称重复，禁止重复新增",
  CONTACT_IMPORT_RECORD_TIME_REQUIRED: "动态内容缺少发生时间",
  CONTACT_IMPORT_SCHOOL_NOT_FOUND: "学校未匹配",
  CONTACT_IMPORT_SCHOOL_AMBIGUOUS: "学校存在多个候选",
  CONTACT_IMPORT_ORG_UNIT_NOT_FOUND: "组织单元未匹配",
  CONTACT_IMPORT_CONTACT_NAME_REQUIRED: "联系人姓名为空",
  CONTACT_IMPORT_PHONE_TOO_LONG: "手机号过长",
  CONTACT_IMPORT_TARGET_CONTACT_REQUIRED: "需要人工选择目标联系人",
  CONTACT_IMPORT_CREATE_BLOCKED_BY_DATABASE: "手机号或联系人全名称已存在，禁止新增",
  CONTACT_IMPORT_BATCH_MOBILE_CONFLICT: "本批次手机号重复，禁止重复新增",
  CONTACT_IMPORT_ACTION_NOT_SUPPORTED: "仅支持新增联系人或同校同名补电话",
  CONTACT_IMPORT_BACKUP_PHONE_TOO_LONG: "追加后的备用电话超过 30 字符，请人工处理，原号码不会截断",
  CONTACT_IMPORT_PHONE_EXISTS_SKIPPED: "电话已存在，本行跳过",
  CONTACT_IMPORT_NO_NEW_PHONE_SKIPPED: "同校同名联系人已存在，源行没有新电话可补充，已跳过",
  CONTACT_IMPORT_PREVIEW_CHANGED: "联系人匹配结果已变化，请重新校验预览后执行",
  CONTACT_IMPORT_UPDATE_SCOPE_DENIED: "没有该联系人的电话修改权限",
  CONTACT_IMPORT_OWNER_NOT_FOUND: "所属主销售未匹配到在职销售人员",
  CONTACT_IMPORT_OWNER_AMBIGUOUS: "所属主销售匹配到多个销售人员",
};

/** 把后端问题码转为操作者可理解的说明。 */
export function describeContactImportIssue(code: string): string {
  return ISSUE_MESSAGES[code] ?? code;
}

/** 更新预览单元格，不允许编辑后端计算字段。 */
export function updateContactImportCell(
  rows: ContactImportRow[],
  rowId: number,
  group: "organization" | "contact" | "record" | "assignment",
  field: string,
  value: unknown,
): ContactImportRow[] {
  return rows.map((row) => {
    if (row.id !== rowId || row.normalized[group]?.[field] === value) return row;
    const editedGroup = { ...row.normalized[group], [field]: value };
    // 名称变更后必须重新匹配，不能沿用上次校验的学校 ID。
    if (group === "organization" && field === "school_name") delete editedGroup.org_id;
    return {
      ...row,
      // 阻断行默认未勾选；修正后保留新增/补电话意图，仍由后端校验决定能否执行。
      selected: row.selected || (row.validationStatus === "blocked" && row.actionType !== "skip"),
      normalized: { ...row.normalized, [group]: editedGroup },
    };
  });
}
